import type { ChangeEvent, ReactNode } from 'react';
import { useEffect, useCallback } from 'react';
import Input from '@creditflowresearch/uilib/Input';
import IconFiltering from '@creditflowresearch/uilib/Icon/IconFiltering';
import useCollectionSearch from './useCollectionSearch';

import styles from './collectionSearch.module.scss';

interface ICollectionSearchProps<T> {
  collection: T[],
  getText: (item: T) => string,
  children: (input: ReactNode, filteredCollection: T[], emptyNode: ReactNode, isSearchActive: boolean) => ReactNode,
  emptyCollection?: (query: string) => ReactNode,
  resetKey?: string | number | boolean,
  placeholder?: string,
  inputName?: string,
  dataTest?: string,
}

const CollectionSearch = <T,>({
  collection,
  getText,
  children,
  emptyCollection,
  resetKey,
  placeholder = 'Search...',
  inputName = 'collectionSearch',
  dataTest = 'collectionSearch',
}: ICollectionSearchProps<T>): ReactNode => {
  const { query, isSearchActive, setQuery, filteredCollection } = useCollectionSearch(collection, getText);

  useEffect(() => {
    setQuery('');
  }, [resetKey]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement> | string): void => setQuery(typeof e === 'string' ? e : e.target.value),
    [setQuery],
  );

  const isEmptyResult = emptyCollection && filteredCollection.length === 0 && isSearchActive;

  const input = (
    <Input
      name={ inputName }
      dataTest={ dataTest }
      placeholder={ placeholder }
      icon={ IconFiltering }
      iconPosition="left"
      isClearable
      formFieldClassName={ styles.searchInputField }
      input={ {
        name: inputName,
        value: query,
        onChange: handleChange,
        onBlur: () => {},
        onFocus: () => {},
      } }
      meta={ {} }
    />
  );

  return <>{ children(input, isEmptyResult ? [] : filteredCollection, isEmptyResult ? emptyCollection!(query) : null, isSearchActive) }</>;
};

export default CollectionSearch;
